import { useState } from "react";
import { Box } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Heading1 } from "../../../common/components/styled/styled";
import ArrowLeft from "../../../common/icons/arrowLeft.svg";
import { useAppDispatch } from "../../../app/hooks";
import {
  editOrderQueue,
  fetchOrderQueueById,
  removeOrderQueue,
} from "../settingsActions";
import { OrderQueueForm } from "./OrderQueueForm";
import { EditOrderQueue, OrderQueue, OrderQueueType } from "./orderQueueApi";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { SpinnerButton } from "../../../common/components/functional/spinnerButton/SpinnerButton";
import { FormContainer } from "../../customer/customerManagement/styles";
import { useConfirmationDialog } from "../../../contexts/ConfirmationDialogContext";

interface Props {
  queueId: string;
  close: () => void;
}

export const OrderQueueDetails = ({ queueId, close }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { confirm } = useConfirmationDialog();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [queueName, setQueueName] = useState<string>("");

  const getDefaultValues = async () => {
    setIsLoading(true);
    const queueAction = await dispatch(fetchOrderQueueById(queueId));
    setIsLoading(false);
    if (isFulfilled(queueAction)) {
      setQueueName(queueAction.payload.name);
      const queue: OrderQueue = queueAction.payload;
      const defaultValues = {
        id: queue.id,
        name: queue.name || "",
        type: queue.type,
        cities:
          queue.cities?.map((city) => ({
            id: city,
            name: city,
          })) || [],
        eezyCompany: {
          id: queue.eezyCompany?.id || "",
          name: queue.eezyCompany?.name || "",
        },
        contracts: queue.contracts,
        members: queue.members.map((member) => ({
          id: member.id,
          name: `${member.firstName} ${member.lastName}`,
        })),
      };
      return defaultValues;
    }
  };

  const methods = useForm({
    defaultValues: async () => await getDefaultValues(),
    mode: "onChange",
  });

  const { reset } = methods;

  const saveQueue = (dirtyFields: [x: string]) => {
    setIsLoading(true);
    const form = methods.getValues();
    const payload = { orderQueueId: queueId };
    Object.keys(dirtyFields).forEach((key) => {
      const value =
        key === "cities" || key === "members"
          ? form[key].map((el: any) => el.id)
          : form[key];
      Object.assign(payload, { [key]: value });
    });
    dispatch(editOrderQueue(payload as EditOrderQueue)).then((action) => {
      setIsLoading(false);
      if (isFulfilled(action)) {
        reset(form);
        setQueueName(form.name);
      }
    });
  };

  const deleteQueue = () => {
    setIsLoading(true);
    dispatch(removeOrderQueue(queueId)).then((action) => {
      if (isFulfilled(action)) {
        setIsLoading(false);
        close();
      }
    });
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: t("orderQueues:deleteQueue"),
      contentText: t("orderQueues:confirmDeleteQueue"),
      confirmText: t("common:delete"),
    });
    if (ok) deleteQueue();
  };

  return (
    <FormContainer maxWidth="930px">
      <OverlaySpinner isLoading={isLoading} />
      <Box display="flex" alignItems="center">
        <Heading1>
          <img
            src={ArrowLeft}
            alt={"< Back: "}
            style={{ marginRight: "16px", cursor: "pointer" }}
            onClick={close}
          />
          {queueName}
        </Heading1>
        <SpinnerButton
          label={t("delete")}
          onClick={handleDelete}
          color="error"
          sx={{ height: "36px", borderRadius: "20px", marginLeft: "16px" }}
          disabled={
            methods.getValues().type === OrderQueueType.DEFAULT ||
            methods.getValues().contracts?.length > 0
          }
        />
      </Box>
      <FormProvider {...methods}>
        <OrderQueueForm onSubmit={saveQueue} isLoading={isLoading} />
      </FormProvider>
    </FormContainer>
  );
};
